import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Award, Landmark, ArrowRight, ShieldCheck } from "lucide-react";

export default function PublicCertificate() {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/certificates/public/${certId}`)
      .then((r) => setCert(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [certId]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-3">Not found</div>
        <h1 className="font-serif text-3xl font-bold text-[#0A0A0A] mb-4">Certificate not found</h1>
        <p className="text-[#525252] mb-6">{error}</p>
        <Link to="/" className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#0A0A0A] text-[#FFFFFF]">YesVersity home</Link>
      </div>
    );
  }

  if (!cert) return <div className="max-w-4xl mx-auto p-12 text-[#525252]">Loading…</div>;

  const issued = new Date(cert.issued_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="max-w-4xl mx-auto px-6 py-14">
      <div className="flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-[0.2em] text-[#525252]">
        <ShieldCheck className="w-3.5 h-3.5 text-[#C5A059]" /> Verified certificate · {cert.cert_id}
      </div>

      <div data-testid="public-certificate-card" className="border-2 border-[#0A0A0A] p-10 md:p-12 bg-[#FFFFFF] relative">
        <div className="absolute top-6 right-6 font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252]">
          {cert.cert_id}
        </div>

        <div className="flex items-center gap-3 mb-12">
          <Landmark className="w-6 h-6 text-[#0A0A0A]" strokeWidth={1.75} />
          <div>
            <div className="font-serif text-lg font-bold text-[#0A0A0A]">YesVersity</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252]">Certificate of completion</div>
          </div>
        </div>

        <div className="mb-10">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-3">This certifies that</div>
          <div data-testid="public-certificate-name" className="font-serif text-4xl md:text-5xl font-black text-[#0A0A0A] mb-8 break-words">{cert.user_name}</div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-3">has completed</div>
          <div className="font-serif text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-2">
            <span className="text-[#C5A059] font-mono text-xl mr-3">{cert.module_number}</span>
            {cert.module_title}
          </div>
        </div>

        <hr className="border-[#E5E5E0] my-8" />

        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] mb-1">Issued</div>
            <div className="font-serif text-lg font-semibold text-[#0A0A0A]">{issued}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] mb-1">Signed</div>
            <div className="font-serif italic text-lg text-[#0A0A0A]">The Faculty</div>
          </div>
          <Award className="w-14 h-14 text-[#C5A059]" strokeWidth={1.25} />
        </div>
      </div>

      <div className="mt-8 p-6 border border-[#E5E5E0] bg-[#F5F5F0] flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="font-serif font-semibold text-[#0A0A0A] mb-1">Want your own?</div>
          <p className="text-sm text-[#525252]">Read the module and earn a verifiable certificate too.</p>
        </div>
        <Link
          data-testid="public-cert-explore"
          to={`/modules/${cert.module_id}`}
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#0A0A0A] text-[#FFFFFF] hover:bg-[#000000] transition-colors font-medium text-sm"
        >
          Explore the module <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
